import { useState, useEffect, type CSSProperties, type ReactNode, useCallback, useRef } from "react";
import { Badge, Button, Nav, Spinner, Table } from "react-bootstrap";

import Loader from "../Loader";
import ErrorComponent from "../ErrorComponent";
import { translate } from "../IntlProviderWrapper";
import { niceDateTime, classNames } from "../../lib/Util";
import { getBackend as getBackend2 } from "../../lib/backend/Backend2";
import type { IGetLatestWorkforceKpiReportRes } from "../../lib/backend/reports.generated.types";
import { useJobStatusPolling } from "../../lib/custom-hooks/useJobStatusPolling";
import {
    mapReportToViewModel,
    type CompetenceTableKpi,
    type CompetenceTableViewModel,
    type WorkshopKpisViewModel,
    type WorkforceKpi,
    type WorkforceKpiValue,
} from "../../lib/WorkforceKPIs";
import { getOrgData, isInRoles, ROLE_ADMIN, ROLE_POWER_USER } from "../../lib/Auth";
import { getLineGroupForUser, waitForManufacturingAsync } from "../../lib/BusinessLogic";

import "./WorkforceKPIs.scss";

const DEFAULT_TIME_RANGE_KEY = "today";
const DEV_MODE = false; // bypass the permission check for testing purposes.

/**
 * Ensures the KPI payload matches the UI context: report plant is the requested plant and,
 * when present, metadata.org_uuid matches the signed-in org (report is not from another tenant).
 * If DEV_MODE is true, returns true.
 */
const isReportValid = (
    report: IGetLatestWorkforceKpiReportRes,
    { orgUuid, plantUuid }: { orgUuid: string; plantUuid: string }
): boolean => {
    if (DEV_MODE) {
        return true;
    }

    return Boolean(
        report.metadata.org_uuid === orgUuid &&
            report.plants[0] &&
            report.plants[0].uuid === plantUuid &&
            report.metadata.time_range_keys &&
            report.metadata.time_range_keys.length > 0
    );
};

/**
 * Filters workshops by permissions.
 * If DEV_MODE is true, returns all workshops.
 */
const filterPermittedWorkshops = (workshops: WorkshopKpisViewModel[]): WorkshopKpisViewModel[] => {
    return DEV_MODE ? workshops : workshops.filter(workshop => getLineGroupForUser(workshop.uuid) !== null);
};

const workforceKpiFieldTranslationId = (fieldKey: string) => {
    const id = fieldKey === "ratioPct" ? "ratio_pct" : fieldKey;
    return `Manufacturing.WorkforceKPIs.field.${id}`;
};

const renderValue = (field: WorkforceKpiValue) => {
    if (field.value === null) {
        return "-";
    }

    const ratio = field.isRatio ? "%" : "";
    const unit = field.unit ? ` ${field.unit}` : "";

    let value: ReactNode = `${field.value}${unit || ratio}`;
    if (field.status) {
        value = <span className={`badge delta-badge variant-${field.status}`}>{value}</span>;
    }
    return value;
};

const renderKpiCards = (kpis: WorkforceKpi[]) => {
    return (
        <div className="d-flex flex-responsive mb-3 gap-3" style={{ "--min-child-width": "12rem" } as CSSProperties}>
            {kpis.map(card => {
                const className = card.kpis.length === 0 ? "card-neutral" : card.status ? `card-${card.status}` : "";
                return (
                    <div key={card.id} className={`card gap-3 ${className}`}>
                        <div className="card-title">{translate(`Manufacturing.WorkforceKPIs.row.${card.id}`)}</div>
                        {card.kpis.length > 0 &&
                            card.kpis.map(metric => (
                                <div key={metric.id} className="card-row align-items-start">
                                    <span className="card-label">
                                        {translate(workforceKpiFieldTranslationId(metric.id))}
                                    </span>
                                    <span className="card-value">{renderValue(metric)}</span>
                                </div>
                            ))}
                        {card.kpis.length === 0 && (
                            <span className="text-danger">
                                <strong>{translate("common.no_data")}</strong>
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const renderCompetenceTable = (competenceTable: CompetenceTableViewModel, options?: { hideCardHeading?: boolean }) => {
    // if there are no competencies, hide the competence table
    if (competenceTable.body.length === 0) {
        return null;
    }

    return (
        <div className="card p-0">
            {!options?.hideCardHeading && (
                <div className="card-title card-title-border-bottom p-3">
                    {translate("Manufacturing.WorkforceKPIs.competenceTable.sectionTitle", "Competences")}
                </div>
            )}
            <div className="card-content">
                <div className="table-responsive">
                    <Table size="sm" responsive className="dashboard-table workforce-kpis-competence-table mb-0">
                        <thead className="table-header-neutral">
                            <tr>
                                {competenceTable.header.map(columnId => (
                                    <th key={columnId} className={columnId === "competence" ? undefined : "text-right"}>
                                        {translate(`Manufacturing.WorkforceKPIs.competenceTable.column.${columnId}`)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {competenceTable.body.map((row, rowIndex) => {
                                const competenceName = row[0].value;
                                const metricCells = row.slice(1) as WorkforceKpiValue[];
                                const columnIds = competenceTable.header.slice(1) as CompetenceTableKpi[];

                                return (
                                    <tr key={rowIndex}>
                                        <td>{competenceName}</td>
                                        {columnIds.map((columnId, columnIndex) => (
                                            <td key={`${competenceName}-${columnId}`} className="text-right">
                                                {renderValue(metricCells[columnIndex])}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            </div>
        </div>
    );
};

type Props = {
    plant_uuid: string;
};

export default function WorkforceKPIs(props: Props) {
    const [report, setReport] = useState<IGetLatestWorkforceKpiReportRes | null>(null);
    const [selectedTimeRangeKey, setSelectedTimeRangeKey] = useState(DEFAULT_TIME_RANGE_KEY);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [rerunStartError, setRerunStartError] = useState<string | null>(null);

    const loadCancelledRef = useRef(false);

    /**
     * Loads the workforce KPI report.
     * @param soft - If true, only sets the error state to null and does not set the loading state to true.
     */
    const load = useCallback(
        async (soft = false) => {
            if (!soft) {
                setLoading(true);
                setError(null);
                setReport(null);
            } else {
                setError(null);
            }
            try {
                const [response] = await Promise.all([
                    getBackend2().reports.getLatestWorkforceKpiReport({
                        plant_uuid: props.plant_uuid,
                    }),
                    waitForManufacturingAsync(),
                ]);
                if (!loadCancelledRef.current) {
                    if (
                        !isReportValid(response, {
                            orgUuid: getOrgData().uuid,
                            plantUuid: props.plant_uuid,
                        })
                    ) {
                        setError(translate("Manufacturing.WorkforceKPIs.no_permission_plant"));
                        setReport(null);
                    } else {
                        // Set selectedTimeRangeKey to the first time range key
                        // if the default is not in the report
                        const timeRangeKeys = response.metadata.time_range_keys ?? [];
                        if (!timeRangeKeys.includes(DEFAULT_TIME_RANGE_KEY)) {
                            setSelectedTimeRangeKey(timeRangeKeys[0]);
                        }
                        setReport(response);
                    }
                }
            } catch (err: unknown) {
                if (!loadCancelledRef.current) {
                    setError(err instanceof Error ? err.message : String(err));
                }
            } finally {
                if (!loadCancelledRef.current && !soft) {
                    setLoading(false);
                }
            }
        },
        [props.plant_uuid]
    );

    useEffect(() => {
        loadCancelledRef.current = false;
        void load();
        return () => {
            loadCancelledRef.current = true;
        };
    }, [load]);

    const {
        startPolling,
        isPolling,
        error: pollError,
    } = useJobStatusPolling({
        onCompleted: () => load(true),
    });

    const rerunError = rerunStartError ?? pollError;

    useEffect(() => {
        if (rerunError) {
            console.error(rerunError);
        }
    }, [rerunError]);

    const handleRerun = useCallback(async () => {
        setRerunStartError(null);
        try {
            const { token } = await getBackend2().reports.runWorkforceKpiReport({});
            startPolling(token);
        } catch (e: unknown) {
            setRerunStartError((e as Error).message);
        }
    }, [startPolling]);

    const viewModel = report ? mapReportToViewModel(report, selectedTimeRangeKey) : null;

    const renderTimeRangesNav = () =>
        viewModel &&
        viewModel.timeRangeKeys.length > 0 && (
            <div className="d-flex">
                <Nav variant="pills" className="flex-wrap" aria-label="Workforce KPI time range">
                    {viewModel.timeRangeKeys.map(key => (
                        <Nav.Item key={key}>
                            <Nav.Link
                                href="#"
                                active={selectedTimeRangeKey === key}
                                onClick={e => {
                                    e.preventDefault();
                                    setSelectedTimeRangeKey(key);
                                }}
                            >
                                {translate(`Manufacturing.WorkforceKPIs.time_range.${key}`)}
                            </Nav.Link>
                        </Nav.Item>
                    ))}
                </Nav>
            </div>
        );

    const renderRerunButton = useCallback(
        (showButton: boolean) => (
            <div className="d-flex flex-column justify-content-center align-items-end gap-2">
                {viewModel?.createdAt && (
                    <span className="align-self-end text-right text-smaller text-muted">
                        {translate("common.last_update")}{" "}
                        <span className={isPolling ? "" : "font-weight-bold"}>
                            {niceDateTime(new Date(viewModel.createdAt))}
                        </span>
                    </span>
                )}
                {showButton && (
                    <>
                        <Button
                            variant="btn"
                            className="rerun-btn d-flex align-items-center"
                            onClick={() => void handleRerun()}
                            disabled={isPolling && !rerunError}
                        >
                            {isPolling && !rerunError && <Spinner animation="border" size="sm" className="mr-2" />}
                            {translate("Manufacturing.WorkforceKPIs.rerunKpiCalculation")}
                        </Button>
                        {rerunError && (
                            <Badge variant="danger">
                                <i className="fas fa-exclamation-triangle"></i> {translate("common.failed")}
                            </Badge>
                        )}
                    </>
                )}
            </div>
        ),
        [viewModel, handleRerun, isPolling, rerunError]
    );

    const renderContent = useCallback(() => {
        if (!viewModel) {
            return <ErrorComponent msg={translate("common.error")} type="error" />;
        }

        const workshopsForUser = filterPermittedWorkshops(viewModel.plant.workshops);

        return (
            <>
                <div className="d-flex flex-column gap-5">
                    {viewModel.plant && (
                        <>
                            <section>
                                <h2 className="content-subtitle">
                                    {translate("Manufacturing.HomeView.kpi.plant_level")}
                                </h2>
                                {renderKpiCards(viewModel.plant.kpis)}
                                {renderCompetenceTable(viewModel.plant.competenceTable)}
                            </section>
                            {workshopsForUser.map(workshop => (
                                <section key={workshop.uuid}>
                                    <h2 className="content-subtitle">{workshop.title}</h2>
                                    {renderKpiCards(workshop.kpis)}
                                    {renderCompetenceTable(workshop.competenceTable)}
                                </section>
                            ))}
                            {workshopsForUser.length === 0 && viewModel.plant.workshops.length > 0 && (
                                <ErrorComponent
                                    msg={translate("Manufacturing.WorkforceKPIs.no_permission_workshops")}
                                    type="warning"
                                />
                            )}
                        </>
                    )}
                </div>
                {viewModel.plant.workshops.length === 0 && (
                    <ErrorComponent
                        msg={translate("Manufacturing.WorkforceKPIs.no_workshop_data_available")}
                        type="error"
                    />
                )}
            </>
        );
    }, [viewModel]);

    if (loading) {
        return <Loader />;
    }

    if (error) {
        return <ErrorComponent msg={error} type="error" className="m-3" />;
    }

    const showRerunButton = isInRoles([ROLE_ADMIN, ROLE_POWER_USER]);

    return (
        <div className="workforce-kpis-page d-flex flex-column gap-3">
            <div className="kpi-header d-flex flex-column gap-2">
                <h1 className="content-title">{translate("Manufacturing.HomeView.kpi.workforce_kpis")}</h1>
                <div className={classNames("d-flex justify-content-between", { "align-items-end": showRerunButton })}>
                    {renderTimeRangesNav()}
                    {renderRerunButton(showRerunButton)}
                </div>
            </div>
            <div className="main-section d-flex flex-column gap-3">
                <main className="p-3">{renderContent()}</main>
            </div>
        </div>
    );
}
